import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import React from 'react'
import { z } from 'zod'

import { Form } from '@/app/component/form'
import SubmitButton from '@/app/component/form/submit-button'

type Props = {
  autoFocus?: boolean
  hideTitle?: boolean
  isSubmitting: boolean
  shouldReset: boolean

  onSubmit: (values: FeedbackFormValues) => void
}

const FeedbackForm: React.FC<Props> = ({
  autoFocus = true,
  hideTitle = false,
  isSubmitting,
  shouldReset,
  onSubmit,
}) => {
  const fields = React.useMemo(
    () =>
      ({
        message: {
          fieldProps: {
            autoFocus,
            name: 'message',
            label: 'Feedback message',
            required: true,
            multiline: true,
            rows: 6,
          },
          type: 'text',
        },
      }) as const,
    [autoFocus],
  )

  return (
    <>
      {!hideTitle && (
        <Typography variant="h6" gutterBottom>
          Send Feedback
        </Typography>
      )}
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        We'd love to hear your thoughts, suggestions, or any issues you've encountered.
      </Typography>
      <Form
        schema={feedbackSchema}
        defaultValues={defaultValues}
        fields={fields}
        shouldReset={shouldReset}
        onSubmit={onSubmit}
        renderActions={({ isDirty }) => (
          <Box display="flex" justifyContent="flex-end" mt={1}>
            <SubmitButton
              isDirty={isDirty}
              isSubmitting={isSubmitting}
              tooltipDirty="Please enter a message"
              tooltipSubmitting="Sending feedback..."
            >
              Send Feedback
            </SubmitButton>
          </Box>
        )}
      />
    </>
  )
}

const feedbackSchema = z.object({
  message: z
    .string({ required_error: 'Message is required' })
    .min(1)
    .refine((val) => val.trim().length > 0, { message: 'Message cannot be empty or only whitespace' }),
})

export type FeedbackFormValues = z.infer<typeof feedbackSchema>

const defaultValues: FeedbackFormValues = { message: '' }

export default FeedbackForm
